import { ApplicantCreatorButton, DashboardComponent, DashboardWithId, LinkTo, NamedExpression, NotificationSender, TableValueExpr, ValueExpr } from '@aidkitorg/types/lib/survey';
import { Dashboard } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { canLockPublish as canLockPublishShared } from './lockUtils';
import { nameForColumn } from '@aidkitorg/types/lib/translation/expr_to_sql';

function hasQueue(components: DashboardComponent[]): boolean {
  return components.some(comp => {
    if ('kind' in comp && comp.kind === 'Queue') return true;
    if ('kind' in comp && comp.kind === 'Dashboard Section' && 'components' in comp) {
      return hasQueue(comp.components);
    }
    return false;
  });
}

export const isReviewDashboard = (dashboard: DashboardWithId): boolean => {
  return dashboard.components ? hasQueue(dashboard.components) : false;
};

/** Deep-check whether any component (or nested child) is from a templated block. */
export function hasTemplatedComponent(components: DashboardComponent[]): boolean {
  return components.some(comp => {
    if ((comp as any)._fromTemplatedBlock === true) return true;
    if ('kind' in comp && comp.kind === 'Dashboard Section' && 'components' in comp) {
      return hasTemplatedComponent(comp.components);
    }
    return false;
  });
}

/** Returns true if a dashboard is itself templated OR contains any templated components. */
export function isDashboardTemplated(dashboard: Pick<Dashboard, '_fromTemplatedBlock' | '_hasTemplatedComponents'> & { rawDistroDashboard?: { components?: DashboardComponent[] } }): boolean {
  if (dashboard._fromTemplatedBlock === true) return true;
  if (dashboard._hasTemplatedComponents === true) return true;
  if (dashboard.rawDistroDashboard?.components) {
    return hasTemplatedComponent(dashboard.rawDistroDashboard.components);
  }
  return false;
}

export function isSystemDashboard(dashboard: Pick<Dashboard, 'created_by_uid' | 'created_by'>): boolean {
  return !dashboard.created_by_uid || dashboard.created_by === 'System';
}

export function canLockPublish(
  dashboard: Pick<Dashboard, 'created_by_uid' | 'created_by'>,
  userInfo: { uid?: string; tags?: string[] }
): boolean {
  return canLockPublishShared(dashboard, userInfo, isSystemDashboard(dashboard));
}

export function canEditSystemDashboard(userInfo: { tags?: string[] }): boolean {
  return (userInfo.tags?.includes('admin') || userInfo.tags?.includes('admin-panel')) ?? false;  
}

/**
 * Gather the LinkTos from a column, when present.
 */
export function gatherLinkTos(col: TableValueExpr | NamedExpression | NotificationSender | ApplicantCreatorButton): LinkTo[] | undefined {
  if (!col || typeof col !== 'object' || !('kind' in col)) return undefined;
  if (col.kind === 'LinkTo') return [col as LinkTo];
  if (col.kind === 'Named Expression') {
    return gatherLinkTos((col as NamedExpression).expression);
  }
  if (col.kind === 'When') {
    const w = col as { when: { then: ValueExpr }[]; otherwise: ValueExpr };
    const linkToInBranches = (w.when?.map((b) => gatherLinkTos(b.then)).filter((l) => l !== undefined) ?? []);
    const linkToInOtherwise = gatherLinkTos(w.otherwise);
    return linkToInBranches.flat().concat(linkToInOtherwise ?? []);
  }
  return undefined;
}

/**
 * Per-row generator for a single LinkTo found inside a table column.
 * Produced by `buildLinkGensByCol` and consumed at cell-render time.
 */
export type LinkTemplateGenerator = {
  /** Column name as it will appear in SQL output (matches the row key). */
  colName: string;
  /** Path template with `:uid:` placeholders, e.g. `/a/:uid:/first_review`. */
  pathTemplate: string;
  /** Regex that matches any URL produced by `pathTemplate`. */
  templateRegex: RegExp;
  /** English label from the LinkTo definition. */
  label: string;
  /** Whether the column header should be hidden. */
  hideHeader: boolean;
  /** Original LinkTo definition (for downstream consumers that need it). */
  linkTo: LinkTo;
};

const REGEX_RESERVED = /[.*+?^${}()|[\]\\]/g;

/** Convert a `:uid:`-style path template into a regex that matches its concrete URLs. */
function templateToRegex(template: string): RegExp {
  const escaped = template.replace(REGEX_RESERVED, '\\$&');
  // `:uid:` contains no regex specials, so it survives escaping unchanged.
  const pattern = escaped.replaceAll(':uid:', '[^/]+');
  return new RegExp('^' + pattern + '$');
}

/** Build the URL path template a LinkTo will produce at runtime. */
function pathTemplateForLinkTo(lt: LinkTo): string {
  switch (lt.destination.kind) {
    case 'Payments Page':
      return '/payments/:uid:';
    case 'Custom Link':
      return lt.destination.path;
    case 'Subsurvey':
      return '/a/:uid:/' + lt.destination.path;
    case 'Dashboard':
      return '/d/' + lt.destination.path;
    default:
      return '/ad/:uid:/' + (lt.destination as { path: string }).path;
  }
}

/**
 * Resolve a runtime href produced by a LinkTo into the corresponding admin-panel URL.
 */
export function toAdminPanelHref(href: string, lt: LinkTo): string {
  if (!href) return href;
  if (href.startsWith('/admin-panel')) return href;

  const ensureAdminPanel = (h: string) =>
    '/admin-panel' + (h.startsWith('/') ? h : '/' + h);

  switch (lt.destination.kind) {
    case 'Payments Page':
    case 'Subsurvey':
    case 'ApplicantIdentities':
      return ensureAdminPanel(href);
    case 'Dashboard':
      // We use the path in Distro as the unique ID. AP uses the ID as the path.
      // For now, just redirect to the dashboards page.
      // We can revisit this if it is high use - likely it's not that useful tho.
      return `/admin-panel/dashboards`;
    case 'Custom Link':
      if (/^https?:\/\//i.test(href)) return href;
      return href.startsWith('/') ? ensureAdminPanel(href) : href;
  }
}

/**
 * Build a `colName -> LinkTemplateGenerator[]` map from a list of table columns.
 *
 * This mirrors the SQL layer's column-naming rules: when a LinkTo is wrapped in
 * a Named Expression, SQL names the column after the NamedExpression (not the
 * LinkTo label), so multiple LinkTos in the same `When` end up under one
 * column, each with its own path template.
 *
 * The returned generators carry a precomputed `templateRegex` so callers can
 * pick the right LinkTo per row without depending on a `uid` field.
 */
export function buildLinkGensByCol(
  columns: readonly (TableValueExpr | NamedExpression | NotificationSender | ApplicantCreatorButton)[] | undefined,
): Record<string, LinkTemplateGenerator[]> {
  const byCol: Record<string, LinkTemplateGenerator[]> = {};
  if (!columns?.length) return byCol;

  for (const configCol of columns) {
    const linkTos = gatherLinkTos(configCol);
    if (!linkTos?.length) continue;

    // Use nameForColumn to derive the column name exactly as the SQL layer does.
    // This handles Named Expressions, bare When expressions, bare LinkTos, etc.
    const colName = nameForColumn(configCol as any);
    if (!colName) continue;

    for (const lt of linkTos) {
      const pathTemplate = pathTemplateForLinkTo(lt);
      const gen: LinkTemplateGenerator = {
        colName,
        pathTemplate,
        templateRegex: templateToRegex(pathTemplate),
        label: lt.label?.en ?? '',
        hideHeader: !!lt.hideColumnHeader,
        linkTo: lt,
      };
      if (!byCol[colName]) byCol[colName] = [];
      if (!byCol[colName].some((g) => g.pathTemplate === pathTemplate)) {
        byCol[colName].push(gen);
      }
    }
  }
  return byCol;
}
